import React, { useMemo, useState } from 'react'
import { WarningIcon } from '@chakra-ui/icons'
import { Box, Button, HStack, Placeholders, Row, Stack, Table2, Text, useTable } from 'src/ui'
import { useCompanyContext } from 'src/companies/routes/company-context'
import { hasPermission } from 'src/utils/permissions'
import {
  type MappingListItem,
  type MappingStatus,
  useGetCQGSymbolTenantCommodityMappings,
} from 'src/api/queries/microservices/trade-commodity-config'
import MappingEditor from './commodity-symbol-mapping-editor'

const EMPTY_MAPPINGS: MappingListItem[] = []

function mappingStatusSummary(status: MappingStatus) {
  if (status.type === 'mapped') {
    return `${status.data.name} (${status.data.sourceId})`
  }
  if (status.type === 'perLocationComplete') return 'Mapped per location'
  if (status.type === 'perLocationPartiallyComplete') {
    return (
      <HStack spacing={2}>
        <WarningIcon color="orange.400" />
        <Text>Partially mapped per location</Text>
      </HStack>
    )
  }
  return '—'
}

export default function CommoditySymbolMappings() {
  const { company } = useCompanyContext()
  const tenantId = company.tenant_id
  const mappingsQuery = useGetCQGSymbolTenantCommodityMappings()
  const [selectedSymbol, setSelectedSymbol] = useState<MappingListItem | null>(null)
  const canEdit = hasPermission('offer_management_edit')

  const mappings = mappingsQuery.data ?? EMPTY_MAPPINGS

  const columns = useMemo(
    () => [
      {
        id: 'symbol',
        accessor: 'categorySymbol',
        Header: 'CQG CAT 1 symbol',
        disableSortBy: true,
      },
      {
        id: 'name',
        accessor: 'categorySymbolName',
        Header: 'Name',
        disableSortBy: true,
      },
      {
        id: 'commodity',
        accessor: 'mappingStatus',
        Header: 'Tenant commodity',
        disableSortBy: true,
        Cell: ({ row }) => mappingStatusSummary(row.original.mappingStatus),
      },
      {
        id: '_actions',
        accessor: '_actions',
        Header: '',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Row justifyContent="flex-end">
            <Button
              size="sm"
              variant="ghost"
              color="primary.500"
              isDisabled={!canEdit}
              onClick={() => setSelectedSymbol(row.original)}
            >
              Edit
            </Button>
          </Row>
        ),
      },
    ],
    [canEdit]
  )

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data: mappings,
    tableOptions: { initialState: { sortBy: [] } },
    columns,
  })

  const isLoading = mappingsQuery.isPending
  const hasError = mappingsQuery.isError

  if (isLoading) return <Placeholders.LoadingState mt={8} />
  if (hasError) return <Placeholders.FailedState mt={8} />

  if (selectedSymbol) {
    return (
      <MappingEditor
        symbol={selectedSymbol}
        tenantId={tenantId}
        canEdit={canEdit}
        onClose={() => setSelectedSymbol(null)}
      />
    )
  }

  return (
    <Stack spacing={4}>
      <Box bg="white" borderRadius={3} boxShadow="md" overflow="hidden">
        <Table2 {...getTableProps()} striped>
          <Table2.Header headerGroups={headerGroups} />
          <Table2.Body rows={rows} getTableBodyProps={getTableBodyProps} prepareRow={prepareRow} />
        </Table2>
      </Box>
    </Stack>
  )
}
